import { Colors } from "@jsq/ds/styles";
import { LogLevel, EffectiveStatus } from "~/hooks/useDataStudioQueries/constants";
import * as styles from "../styles_vanilla.css";

export interface StatusDisplay {
  text: string;
  color: string;
}

export interface TaskLog {
  taskId: string;
  executionDate: string;
  tryNumber: number;
  state: string;
  logEntries: Array<{
    timestamp: string;
    message: string;
  }>;
}

const STATUS_DISPLAY_MAP: Record<string, StatusDisplay> = {
  [EffectiveStatus.IN_PROGRESS]: { text: "In Progress", color: Colors.Primary600 },
  [EffectiveStatus.COMPLETED]: { text: "Complete", color: Colors.Success600 },
  [EffectiveStatus.FAILED]: { text: "Failed", color: Colors.Danger600 },
};

export const getStatusDisplay = (jobStatus: string): StatusDisplay => {
  return STATUS_DISPLAY_MAP[jobStatus] || { text: jobStatus, color: Colors.Neutral600 };
};

export const deriveStatusFromTaskLogs = (taskLogs: TaskLog[]): EffectiveStatus => {
  if (!taskLogs || taskLogs.length === 0) {
    return EffectiveStatus.IN_PROGRESS;
  }

  const hasFailedTask = taskLogs.some((task) => {
    const state = task.state.toLowerCase();
    return state === "failed" || state === "blocked by previous step" || state === "cancelled";
  });
  if (hasFailedTask) {
    return EffectiveStatus.FAILED;
  }

  const allTasksSuccessful = taskLogs.every((task) => task.state.toLowerCase() === "success");
  if (allTasksSuccessful) {
    return EffectiveStatus.COMPLETED;
  }

  return EffectiveStatus.IN_PROGRESS;
};

const STATUS_TAG_CLASS_MAP: Record<string, string> = {
  [Colors.Success600]: styles.statusTagSuccess,
  [Colors.Danger600]: styles.statusTagDanger,
  [Colors.Primary600]: styles.statusTagPrimary,
};

export const getStatusTagClassName = (color: string): string => {
  return STATUS_TAG_CLASS_MAP[color] || styles.statusTagPrimary;
};

export enum StatusVariantEnum {
  SUCCESS = "success",
  ERROR = "error",
  WARNING = "warning",
}

const TASK_STATUS_VARIANT_MAP: Record<string, StatusVariantEnum> = {
  success: StatusVariantEnum.SUCCESS,
  failed: StatusVariantEnum.ERROR,
};

export const getTaskStatusVariant = (state: string): StatusVariantEnum => {
  const lowerState = state.toLowerCase();
  return TASK_STATUS_VARIANT_MAP[lowerState] || StatusVariantEnum.WARNING;
};

export const getLogLevelClassName = (level: string): string => {
  const upperLevel = level.toUpperCase();

  switch (upperLevel) {
    case LogLevel.ERROR:
    case LogLevel.CRITICAL:
      return `${styles.logLevel} ${styles.logLevelError}`;
    case LogLevel.WARNING:
    case LogLevel.WARN:
      return `${styles.logLevel} ${styles.logLevelWarning}`;
    case LogLevel.DEBUG:
      return `${styles.logLevel} ${styles.logLevelDebug}`;
    case LogLevel.INFO:
    default:
      return `${styles.logLevel} ${styles.logLevelInfo}`;
  }
};
